import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus,
    faTrash,
    faSearch,
    faSpinner,
    faUsers,
    faLink,
    faImage,
} from '@fortawesome/free-solid-svg-icons';
import { toast, ToastContainer } from 'react-toastify';
import { talentsService } from '../../services/talentsService';
import TalentForm from './TalentForm';
import './TalentsManagement.css';

function TalentsManagement({ currentUser }) {
    const [talents, setTalents] = useState([]);
    const [filteredTalents, setFilteredTalents] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showTalentForm, setShowTalentForm] = useState(false);
    const [selectedTalent, setSelectedTalent] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [talentsPerPage] = useState(10);
    const [sortField, setSortField] = useState('Title');
    const [sortDirection, setSortDirection] = useState('asc');

    useEffect(() => {
        fetchTalents();
    }, []);

    useEffect(() => {
        applyFilters();
    }, [talents, searchTerm, sortField, sortDirection]);

    const fetchTalents = async () => {
        setIsLoading(true);
        try {
            const talentsData = await talentsService.getAllTalents();
            const processed = talentsData.map(item => ({
                ...item,
                ID: item.ID || item.id,
            }));
            setTalents(processed);
        } catch (error) {
            console.error('Error fetching talents:', error);
            toast.error('Failed to load resources');
        } finally {
            setIsLoading(false);
        }
    };

    const applyFilters = () => {
        let filtered = [...talents];

        if (searchTerm.trim()) {
            const query = searchTerm.toLowerCase();
            filtered = filtered.filter(item =>
                item.Title?.toLowerCase().includes(query) ||
                item.Text?.replace(/<[^>]*>/g, '').toLowerCase().includes(query)
            );
        }

        filtered.sort((a, b) => {
            const aValue = (a[sortField] || '').toString().toLowerCase();
            const bValue = (b[sortField] || '').toString().toLowerCase();
            if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });

        setFilteredTalents(filtered);
        setCurrentPage(1);
    };

    const handleAddTalent = () => {
        setSelectedTalent(null);
        setShowTalentForm(true);
    };

    const handleEditTalent = (talent) => {
        setSelectedTalent(talent);
        setShowTalentForm(true);
    };

    const handleDeleteTalent = async (talentId) => {
        if (!window.confirm('Are you sure you want to delete this item?')) {
            return;
        }
        try {
            await talentsService.deleteTalent(talentId);
            toast.success('Item deleted successfully');
            await fetchTalents();
        } catch (error) {
            console.error('Error deleting talent:', error);
            toast.error('Failed to delete item');
        }
    };

    const handleTalentSave = async (talentData) => {
        try {
            if (talentData.ID) {
                await talentsService.updateTalent(talentData.ID, talentData);
                toast.success('Item updated successfully');
            } else {
                await talentsService.createTalent(talentData);
                toast.success('Item created successfully');
            }
            setShowTalentForm(false);
            setSelectedTalent(null);
            await fetchTalents();
        } catch (error) {
            console.error('Error saving talent:', error);
            toast.error('Failed to save item');
            throw error;
        }
    };

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const getSortIcon = (field) => {
        if (sortField !== field) return null;
        return sortDirection === 'asc' ? '↑' : '↓';
    };

    // Pagination
    const indexOfLast = currentPage * talentsPerPage;
    const indexOfFirst = indexOfLast - talentsPerPage;
    const currentTalents = filteredTalents.slice(indexOfFirst, indexOfLast);
    const totalPages = Math.ceil(filteredTalents.length / talentsPerPage);
    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    return (
        <div className="talents-management">
            <ToastContainer />
            {isLoading ? (
                <div className="talents-loading">
                    <FontAwesomeIcon icon={faSpinner} spin />
                    <span>Loading resources...</span>
                </div>
            ) : (
            <>
            <div className="talents-management-header">
                <button className="btn-add-talent" onClick={handleAddTalent}>
                    <FontAwesomeIcon icon={faPlus} /> Add Resource
                </button>
            </div>

            {/* Statistics */}
            <div className="talents-stats">
                <div className="stat-card">
                    <div className="stat-icon">
                        <FontAwesomeIcon icon={faUsers} />
                    </div>
                    <div className="stat-details">
                        <h4>Total Resources</h4>
                        <p className="stat-number">{talents.length}</p>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon icon-image">
                        <FontAwesomeIcon icon={faImage} />
                    </div>
                    <div className="stat-details">
                        <h4>With Image</h4>
                        <p className="stat-number">
                            {talents.filter(t => t.Image).length}
                        </p>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon icon-link">
                        <FontAwesomeIcon icon={faLink} />
                    </div>
                    <div className="stat-details">
                        <h4>With Link</h4>
                        <p className="stat-number">
                            {talents.filter(t => t.Link).length}
                        </p>
                    </div>
                </div>
            </div>

            {/* Search */}
            <div className="talents-toolbar">
                <div className="search-box">
                    <FontAwesomeIcon icon={faSearch} />
                    <input
                        type="text"
                        placeholder="Search resources..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            {/* Talents Table */}
            <div className="talents-table-container">
                {currentTalents.length === 0 ? (
                    <div className="no-talents">
                        <FontAwesomeIcon icon={faUsers} size="3x" />
                        <h4>No Resources Found</h4>
                        <p>No resources match your current search.</p>
                        <button className="btn-add-talent" onClick={handleAddTalent}>
                            <FontAwesomeIcon icon={faPlus} /> Add Resource
                        </button>
                    </div>
                ) : (
                    <>
                        <table className="talents-table">
                            <thead>
                                <tr>
                                    <th
                                        onClick={() => handleSort('Title')}
                                        className={`sortable ${sortField === 'Title' ? 'active' : ''}`}
                                    >
                                        Title {getSortIcon('Title')}
                                    </th>
                                    <th>Text</th>
                                    <th
                                        onClick={() => handleSort('Image')}
                                        className={`sortable ${sortField === 'Image' ? 'active' : ''}`}
                                    >
                                        Image {getSortIcon('Image')}
                                    </th>
                                    <th>Link</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentTalents.map(talent => (
                                    <tr key={talent.ID} onClick={() => handleEditTalent(talent)} style={{ cursor: 'pointer' }}>
                                        <td>
                                            <div className="talent-title-cell">
                                                <h4>{talent.Title}</h4>
                                            </div>
                                        </td>
                                        <td>
                                            <p className="talent-text-preview">
                                                {talent.Text ? (() => { const plain = talent.Text.replace(/<[^>]*>/g, ''); return `${plain.substring(0, 400)}${plain.length > 400 ? '...' : ''}`; })() : '—'}
                                            </p>
                                        </td>
                                        <td>
                                            {talent.Image ? (
                                                <span className="image-filename">
                                                    <FontAwesomeIcon icon={faImage} /> {talent.Image}
                                                </span>
                                            ) : '—'}
                                        </td>
                                        <td>
                                            {talent.Link ? (
                                                <a
                                                    href={talent.Link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="talent-link"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <FontAwesomeIcon icon={faLink} /> View
                                                </a>
                                            ) : '—'}
                                        </td>
                                        <td>
                                            <span className={`status-badge ${talent.Published === true || talent.Published === 'Yes' ? 'published' : 'draft'}`}>
                                                {talent.Published === true || talent.Published === 'Yes' ? 'Published' : 'Draft'}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="table-actions">
                                                <button
                                                    className="btn-icon btn-danger"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteTalent(talent.ID);
                                                    }}
                                                    title="Delete"
                                                >
                                                    <FontAwesomeIcon icon={faTrash} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="pagination">
                                <button
                                    onClick={() => paginate(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="pagination-btn"
                                >
                                    Previous
                                </button>
                                {[...Array(totalPages)].map((_, index) => (
                                    <button
                                        key={index + 1}
                                        onClick={() => paginate(index + 1)}
                                        className={`pagination-btn ${currentPage === index + 1 ? 'active' : ''}`}
                                    >
                                        {index + 1}
                                    </button>
                                ))}
                                <button
                                    onClick={() => paginate(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="pagination-btn"
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Talent Form Modal */}
            {showTalentForm && (
                <TalentForm
                    talent={selectedTalent}
                    onSave={handleTalentSave}
                    onClose={() => {
                        setShowTalentForm(false);
                        setSelectedTalent(null);
                    }}
                />
            )}
            </>
            )}
        </div>
    );
}

export default TalentsManagement;
